'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/components/PublicFooter';
import {
  HelpCircle, ChevronDown, ChevronUp, Send, Loader2,
  BookOpen, CreditCard, Truck, RefreshCw, MessageSquare,
  Phone, Mail, CheckCircle, AlertCircle, Package,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ── FAQ Data ──────────────────────────────────────────────────────────────────

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface FaqSection {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  items: FaqItem[];
}

const FAQ_SECTIONS: FaqSection[] = [
  {
    id: 'booking',
    label: 'Booking Help',
    icon: BookOpen,
    color: 'text-blue-400',
    items: [
      {
        id: 'b1',
        question: 'How do I book an ad placement?',
        answer: 'Click "Book an Ad" in the top navigation or visit /order-wizard. You\'ll walk through a step-by-step process: choose your placement type (Full Page), select your target regions, pick your issue dates, upload or request artwork, and complete payment. The whole process takes under 10 minutes.',
      },
      {
        id: 'b2',
        question: 'What placement sizes are available?',
        answer: 'We currently offer Full Page placements (8.5 × 5.5 inches). Half Page placements are coming soon. Each placement reaches 1,000+ businesses per region per issue.',
      },
      {
        id: 'b3',
        question: 'Can I book multiple regions at once?',
        answer: 'Yes! During the order wizard you can select multiple regions. Each region is priced separately. Bundle pricing is available when you commit to 3 or more consecutive issues.',
      },
      {
        id: 'b4',
        question: 'What is the artwork submission deadline?',
        answer: 'Artwork must be submitted at least 7 business days before the issue print date. You\'ll receive email reminders as the deadline approaches. Late submissions may result in your ad being moved to the next available issue.',
      },
      {
        id: 'b5',
        question: 'Can I request a design service?',
        answer: 'Yes. During the order wizard, select "I need design help" and our design team will reach out within 1 business day. Design services are available for an additional fee. You can also visit the Design Service page directly.',
      },
    ],
  },
  {
    id: 'payment',
    label: 'Payment',
    icon: CreditCard,
    color: 'text-amber-400',
    items: [
      {
        id: 'p1',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) processed securely through Stripe. We do not store your card details — all payment data is handled by Stripe\'s PCI-compliant infrastructure.',
      },
      {
        id: 'p2',
        question: 'Is my payment information secure?',
        answer: 'Yes. All payments are processed by Stripe, a PCI DSS Level 1 certified payment processor. Your card details are never stored on our servers. Transactions are encrypted using TLS 1.3.',
      },
      {
        id: 'p3',
        question: 'Can I get an invoice for my purchase?',
        answer: 'Yes. Invoices are automatically generated and emailed to you after payment. You can also download invoices from your Order Dashboard under the "Invoices" tab.',
      },
      {
        id: 'p4',
        question: 'What happens if my payment fails?',
        answer: 'If your payment fails, your order will not be confirmed. You\'ll receive an email notification with instructions to retry. Common causes include insufficient funds, expired cards, or bank security blocks. Contact your bank if the issue persists.',
      },
      {
        id: 'p5',
        question: 'Do you offer bundle pricing?',
        answer: 'Yes. When you book 3 or more consecutive issues in the same region, you receive a discounted bundle rate. Bundle pricing is shown automatically in the order wizard when you select multiple issue dates.',
      },
    ],
  },
  {
    id: 'delivery',
    label: 'Delivery Tracking',
    icon: Truck,
    color: 'text-green-400',
    items: [
      {
        id: 'd1',
        question: 'How do I track my campaign delivery?',
        answer: 'Log in and visit your Campaign Performance dashboard. You\'ll see real-time delivery status, driver check-ins, and proof-of-delivery photos as your books are distributed across your selected regions.',
      },
      {
        id: 'd2',
        question: 'How many businesses receive each issue?',
        answer: 'Each region targets 1,000+ businesses. Actual delivery counts may vary by ±10% due to business closures, access restrictions, or route adjustments. Your dashboard shows the confirmed delivery count after distribution is complete.',
      },
      {
        id: 'd3',
        question: 'How long does distribution take?',
        answer: 'Distribution for each issue typically takes 3–5 business days per region. You\'ll receive email updates when distribution begins and when it\'s complete. Proof-of-delivery photos are uploaded by drivers in real time.',
      },
      {
        id: 'd4',
        question: 'Can I see proof of delivery?',
        answer: 'Yes. Drivers upload geo-tagged photos as they deliver to each stop. These are visible in your Campaign Performance dashboard under the delivery proof section for each issue.',
      },
      {
        id: 'd5',
        question: 'What regions do you currently cover?',
        answer: 'We currently cover multiple regions across Florida and Puerto Rico. Visit our Regions & Pricing page for a full list of active regions, coverage maps, and pricing per region.',
      },
    ],
  },
  {
    id: 'refunds',
    label: 'Refunds & Credits',
    icon: RefreshCw,
    color: 'text-purple-400',
    items: [
      {
        id: 'r1',
        question: 'What is your refund policy?',
        answer: 'All sales are final — we do not issue cash refunds. However, if you cancel before your artwork is approved and sent to print, we will issue a full account credit equal to the amount paid. Credits can be applied to any future booking and expire 12 months from issuance.',
      },
      {
        id: 'r2',
        question: 'How do I cancel an order?',
        answer: 'To cancel an order, go to your Order Dashboard, find the order, and click "Cancel Order." Cancellations are only possible before artwork approval. Once your ad has been sent to print, cancellation is not available.',
      },
      {
        id: 'r3',
        question: 'What if there was a printing error?',
        answer: 'If a printing or distribution error was caused by ZipAdBook (e.g., wrong artwork printed, significant under-delivery), we will offer a complimentary re-run in a future issue at no charge. Please contact support with your order number and a description of the issue.',
      },
      {
        id: 'r4',
        question: 'How long does it take to receive a credit?',
        answer: 'Account credits are applied immediately upon cancellation approval. You\'ll receive an email confirmation. Credits appear in your account and are automatically applied at checkout on your next order.',
      },
      {
        id: 'r5',
        question: 'Can I transfer my credit to another account?',
        answer: 'Credits are non-transferable and tied to the account that made the original purchase. If you have a special circumstance, please contact our support team and we\'ll review your case.',
      },
    ],
  },
];

// ── Contact Form ──────────────────────────────────────────────────────────────

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

const CONTACT_CATEGORIES = [
  { value: 'booking', label: 'Booking Help' },
  { value: 'payment', label: 'Payment Issue' },
  { value: 'delivery', label: 'Delivery / Campaign' },
  { value: 'refund', label: 'Refund / Credit' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'other', label: 'Other' },
];

// ── FAQ Accordion ─────────────────────────────────────────────────────────────

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.id} className="rounded-xl border border-white/8 bg-navy-800/50 overflow-hidden">
          <button
            onClick={() => setOpenId(openId === item.id ? null : item.id)}
            className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/3 transition-colors"
          >
            <span className="text-sm font-medium text-white">{item.question}</span>
            {openId === item.id ? (
              <ChevronUp size={16} className="text-brand-400 shrink-0" />
            ) : (
              <ChevronDown size={16} className="text-slate-500 shrink-0" />
            )}
          </button>
          {openId === item.id && (
            <div className="px-5 pb-4 border-t border-white/6">
              <p className="text-sm text-slate-400 leading-relaxed pt-3">{item.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CustomerSupportPage() {
  const [activeSection, setActiveSection] = useState<string>('booking');
  const [formData, setFormData] = useState<ContactFormData>({
    name: '', email: '', subject: '', category: 'booking', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const currentSection = FAQ_SECTIONS.find((s) => s.id === activeSection) ?? FAQ_SECTIONS[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) return;

    setSubmitting(true);
    setSubmitStatus('idle');
    setErrorMsg('');

    try {
      const res = await fetch('/api/help/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: `[${formData.category.toUpperCase()}] ${formData.subject.trim()}`,
          message: formData.message.trim(),
          type: 'support',
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message');

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', category: 'booking', message: '' });
    } catch (err: any) {
      setSubmitStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col">
      <PublicTopbar />
      <main className="flex-1 pt-20 pb-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Header */}
          <div className="text-center mb-12">
            <span className="chip mb-3">Support Center</span>
            <h1 className="font-display text-3xl sm:text-4xl font-800 text-white mb-3">
              How can we <span className="text-gradient">help you?</span>
            </h1>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              Browse our FAQ sections below or send us a message and our team will get back to you within 1 business day.
            </p>
          </div>

          {/* Quick contact strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            <a href="tel:+13212653177" className="flex items-center gap-3 p-4 rounded-xl bg-navy-800 border border-white/8 hover:border-brand-500/30 transition-colors group">
              <div className="w-9 h-9 rounded-lg bg-brand-500/15 flex items-center justify-center">
                <Phone size={16} className="text-brand-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Call Us</p>
                <p className="text-sm font-medium text-white group-hover:text-brand-300 transition-colors">+1 (321) 265-3177</p>
              </div>
            </a>
            <a href="mailto:support@zipadbook.com" className="flex items-center gap-3 p-4 rounded-xl bg-navy-800 border border-white/8 hover:border-brand-500/30 transition-colors group">
              <div className="w-9 h-9 rounded-lg bg-brand-500/15 flex items-center justify-center">
                <Mail size={16} className="text-brand-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Email Support</p>
                <p className="text-sm font-medium text-white group-hover:text-brand-300 transition-colors">support@zipadbook.com</p>
              </div>
            </a>
            <Link href="/support" className="flex items-center gap-3 p-4 rounded-xl bg-navy-800 border border-white/8 hover:border-brand-500/30 transition-colors group">
              <div className="w-9 h-9 rounded-lg bg-brand-500/15 flex items-center justify-center">
                <MessageSquare size={16} className="text-brand-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Live Messaging</p>
                <p className="text-sm font-medium text-white group-hover:text-brand-300 transition-colors">Open Support Chat</p>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left: FAQ */}
            <div className="lg:col-span-2">
              <h2 className="font-display text-xl font-700 text-white mb-5">Frequently Asked Questions</h2>

              {/* Section tabs */}
              <div className="flex flex-wrap gap-2 mb-6">
                {FAQ_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-brand-500 text-white' :'bg-navy-800 border border-white/8 text-slate-400 hover:text-white hover:border-white/15'
                      }`}
                    >
                      <Icon size={14} className={isActive ? 'text-white' : section.color} />
                      {section.label}
                    </button>
                  );
                })}
              </div>

              {/* FAQ items */}
              <FaqAccordion items={currentSection.items} />

              {/* Still need help */}
              <div className="mt-6 p-4 rounded-xl bg-brand-500/8 border border-brand-500/20 flex items-center gap-3">
                <HelpCircle size={18} className="text-brand-400 shrink-0" />
                <p className="text-sm text-slate-400">
                  Can't find your answer?{' '}
                  <button
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-brand-400 hover:text-brand-300 transition-colors font-medium"
                  >
                    Send us a message below
                  </button>
                  {' '}or{' '}
                  <Link href="/support" className="text-brand-400 hover:text-brand-300 transition-colors font-medium">
                    open a support ticket
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div id="contact-form">
              <h2 className="font-display text-xl font-700 text-white mb-5">Send Us a Message</h2>
              <div className="bg-navy-800 border border-white/8 rounded-2xl p-6">

                {submitStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 rounded-full bg-green-500/15 border border-green-500/25 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={24} className="text-green-400" />
                    </div>
                    <h3 className="font-display font-700 text-white text-lg mb-2">Message Sent!</h3>
                    <p className="text-sm text-slate-400 mb-5">
                      Our team will get back to you at your email address within 1 business day.
                    </p>
                    <button
                      onClick={() => setSubmitStatus('idle')}
                      className="text-sm text-brand-400 hover:text-brand-300 transition-colors"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {submitStatus === 'error' && (
                      <div className="flex items-center gap-2.5 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                        <AlertCircle size={15} className="text-red-400 shrink-0" />
                        <p className="text-xs text-red-400">{errorMsg}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Your Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                        required
                        placeholder="Jane Smith"
                        className="w-full px-3.5 py-2.5 bg-navy-700 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                        required
                        placeholder="jane@example.com"
                        className="w-full px-3.5 py-2.5 bg-navy-700 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                        className="w-full px-3.5 py-2.5 bg-navy-700 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-brand-500 transition-colors"
                      >
                        {CONTACT_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Subject</label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))}
                        required
                        placeholder="Brief description of your issue"
                        className="w-full px-3.5 py-2.5 bg-navy-700 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Message</label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                        required
                        rows={5}
                        placeholder="Please describe your issue in detail. Include your order number if applicable."
                        className="w-full px-3.5 py-2.5 bg-navy-700 border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          <Send size={15} />
                          Send Message
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-500 text-center">
                      We typically respond within 1 business day.
                    </p>
                  </form>
                )}
              </div>

              {/* Order status quick link */}
              <div className="mt-4 p-4 rounded-xl bg-navy-800 border border-white/8">
                <div className="flex items-center gap-2.5 mb-2">
                  <Package size={15} className="text-brand-400" />
                  <p className="text-sm font-medium text-white">Track Your Order</p>
                </div>
                <p className="text-xs text-slate-500 mb-3">Log in to view real-time order status and delivery updates.</p>
                <Link
                  href="/order-dashboard"
                  className="inline-flex items-center gap-1.5 text-xs text-brand-400 hover:text-brand-300 transition-colors font-medium"
                >
                  Go to Order Dashboard →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
